import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "./styles/Navbar.css";

export default function Navbar() {
  const { user, logout } = useContext(AuthContext);

  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const location = useLocation();

  const profileRef = useRef(null);
  const menuRef = useRef(null);
  const backdropRef = useRef(null);

  const isAdmin = user?.role === "admin" || user?.role === "super-admin";
  const isManager = user?.role === "Manager" || isAdmin;
  const isSuperAdmin = user?.role === "super-admin";
  const isFinanceHead = user?.role === "finance-head" || isManager;

  const isActive = (path) => location.pathname.startsWith(path);

  const avatarUrl = useMemo(() => {
    return user?.avatar || user?.profilePic || user?.photoUrl || user?.image || "";
  }, [user]);

  const closeAll = () => {
    setMenuOpen(false);
    setProfileOpen(false);
  };

  useEffect(() => {
    closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (e) => {
      const t = e.target;

      if (profileOpen && profileRef.current && !profileRef.current.contains(t)) {
        setProfileOpen(false);
      }

      if (menuOpen && menuRef.current && !menuRef.current.contains(t)) {
        if (backdropRef.current && backdropRef.current.contains(t)) {
          setMenuOpen(false);
        }
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [menuOpen, profileOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAll();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  function Avatar() {
    const [imgOk, setImgOk] = useState(!!avatarUrl);

    useEffect(() => {
      setImgOk(!!avatarUrl);
    }, [avatarUrl]);

    if (imgOk && avatarUrl) {
      return (
        <img
          className="user-avatar-img"
          src={avatarUrl}
          alt="User Avatar"
          onError={() => setImgOk(false)}
          draggable={false}
        />
      );
    }

    return (
      <div className="user-avatar">
        {user?.username?.charAt(0)?.toUpperCase() || "?"}
      </div>
    );
  }

  const handleLogout = () => {
    closeAll();
    logout?.();
  };

  const HOME_AFTER_LOGIN = "/finance";

  return (
    <nav className="main-nav">
      <div className="nav-inner">
        <Link to={HOME_AFTER_LOGIN} className="logo" onClick={closeAll}>
          <img src={require("./assets/logo1.png")} alt="Arosia Logo" />
          <div className="logo-sub">
            <div className="ops-text">TEAMS</div>
            <div className="ops-text">DASHBOARD</div>
          </div>
        </Link>

        <button
          type="button"
          className="menu-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((p) => !p);
            setProfileOpen(false);
          }}
          aria-label="Toggle Menu"
          aria-expanded={menuOpen}
        >
          <span className={`hamburger ${menuOpen ? "active" : ""}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        <div
          ref={backdropRef}
          className={`nav-backdrop ${menuOpen ? "show" : ""}`}
          onClick={() => setMenuOpen(false)}
        />

        <ul
          ref={menuRef}
          className={`nav-links ${menuOpen ? "show" : ""} ${user ? "has-user-block" : ""}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <li>
            <Link
              to="/daily-tasks"
              className={isActive("/daily-tasks") ? "active" : ""}
              onClick={closeAll}
            >
              Daily Tasks
            </Link>
          </li>

          <li>
            <Link
              to="/technician"
              className={isActive("/technician") ? "active" : ""}
              onClick={closeAll}
            >
              Technician
            </Link>
          </li>

          <li>
            <Link
              to="/finance"
              className={isActive("/finance") ? "active" : ""}
              onClick={closeAll}
            >
              Finance
            </Link>
          </li>


          {isFinanceHead && (
            <li>
              <Link
                to="/payment"
                className={isActive("/payment") ? "active" : ""}
                onClick={closeAll}
              >
                Payments
              </Link>
            </li>
          )}
          {isFinanceHead
            && <li>
              <Link
                to="/amc"
                className={isActive("/amc") ? "active" : ""}
                onClick={closeAll}
              >AMC</Link>
            </li>   
          }             
          {isManager && (
            <li>
              <Link
                to="/user-manager"
                className={isActive("/user-manager") ? "active" : ""}
                onClick={closeAll}
              >
                Users
              </Link>
            </li>
          )}

          {isSuperAdmin && (
            <li>
              <Link
                to="/role"
                className={isActive("/role") ? "active" : ""}
                onClick={closeAll}
              >
                Roles
              </Link>
            </li>
          )}

          {isAdmin && (
            <li>
              <Link
                to="/uploads-manager"
                className={isActive("/uploads-manager") ? "active" : ""}
                onClick={closeAll}
              >
                Uploads
              </Link>
            </li>
          )}



          <li>
            <Link
              to="/works"
              className={isActive("/works") ? "active" : ""}
              onClick={closeAll}
            >
              Works
            </Link>
          </li>

          <li>
            <Link
              to="/master-site-manager"
              className={isActive("/master-site-manager") ? "active" : ""}
              onClick={closeAll}
            >
              Masters
            </Link>
          </li>

          <li>
            <Link
              to="/masterandsite"
              className={isActive("/masterandsite") ? "active" : ""}
              onClick={closeAll}
            >
              Sites
            </Link>
          </li>


          <li>
            <Link
              to="/parts"
              className={isActive("/parts") ? "active" : ""}
              onClick={closeAll}
            >
              Parts
            </Link>
          </li>

          {user && (
            <li className="mobile-user-block">
              <div className="mobile-user-card">
                <div className="avatar-wrap avatar-wrap-lg">
                  <Avatar />
                </div>

                <div className="mobile-user-text">
                  <div className="mobile-user-name">{user.username}</div>
                  <div className="mobile-user-role">{user.role}</div>
                </div>
              </div>

              <div className="mobile-actions">
                <Link
                  to="/profile"
                  className="mobile-action-btn"
                  onClick={closeAll}
                >
                  Profile
                </Link>

                <button
                  type="button"
                  className="mobile-action-btn danger"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </li>
          )}
        </ul>

        {user && (
          <div
            className="profile-wrapper"
            ref={profileRef}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="user-pill"
              onClick={() => {
                setProfileOpen((p) => !p);
                setMenuOpen(false);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setProfileOpen((p) => !p);
                  setMenuOpen(false);
                }
              }}
            >
              <div className="avatar-wrap">
                <Avatar />
              </div>

              <div className="user-details">
                <div className="user-name">{user.username}</div>
                <div className="user-role">{user.role}</div>
              </div>
            </div>

            {profileOpen && (
              <div className="profile-dropdown">
                <Link
                  to="/profile"
                  className="dropdown-item"
                  onClick={() => setProfileOpen(false)}
                >
                  Profile
                </Link>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="dropdown-item logout-item"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}